import { useRef, useEffect } from 'react';
import { 
  Box, 
  Checkbox, 
  TextField
} from '@mui/material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { styled } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import MoreVertIcon from '@mui/icons-material/MoreVert';

export default function CheckboxList({ 
  items, 
  onItemsChange, 
  placeholder
}) {
  const inputRefs = useRef({});

  useEffect(() => {
    if (!items || items.length === 0) {
      onItemsChange([{ text: '', checked: false }]);
    }
  }, []);

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    
    const newItems = Array.from(items);
    const [removed] = newItems.splice(result.source.index, 1);
    newItems.splice(result.destination.index, 0, removed);
    
    onItemsChange(newItems);
  };

  const handleTextChange = (index, newText) => {
    const newItems = [...items];
    newItems[index].text = newText;
    onItemsChange(newItems);
  };

  const handleCheckboxChange = (index) => {
    const newItems = [...items];
    newItems[index].checked = !newItems[index].checked;
    onItemsChange(newItems);
  };

  const handleKeyDown = (e, index) => {
    const input = e.target;
    const cursorPosition = input.selectionStart;
    
    if (e.key === 'Enter') {
      e.preventDefault();
      const currentText = items[index].text;
      
      const textBeforeCursor = currentText.substring(0, cursorPosition);
      const textAfterCursor = currentText.substring(cursorPosition);
      
      const newItems = [...items];
      newItems[index].text = textBeforeCursor;
      newItems.splice(index + 1, 0, { 
        text: textAfterCursor, 
        checked: false 
      });
      
      onItemsChange(newItems);

      setTimeout(() => {
        const newInput = inputRefs.current[index + 1];
        if (newInput) {
          newInput.focus();
          newInput.setSelectionRange(0, 0);
        }
      }, 0);
    } else if (e.key === 'Backspace') {
      if (cursorPosition === 0 && index > 0) {
        e.preventDefault();
        const currentText = items[index].text;
        const prevText = items[index - 1].text;
        
        const newItems = [...items];
        newItems[index - 1].text = prevText + currentText;
        newItems.splice(index, 1);
        
        onItemsChange(newItems);

        setTimeout(() => {
          const prevInput = inputRefs.current[index - 1];
          if (prevInput) {
            prevInput.focus();
            prevInput.setSelectionRange(prevText.length, prevText.length);
          }
        }, 0);
      }
    }
  };

  if (!items) return null;

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId="checkboxList">
        {(provided) => (
          <Box
            {...provided.droppableProps}
            ref={provided.innerRef}
            sx={{ pl: 3 }}
          >
            {items.map((item, index) => (
              <Draggable
                key={`item-${index}`}
                draggableId={`item-${index}`}
                index={index}
              >
                {(provided, snapshot) => (
                  <Box
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      mb: 0.5,
                      backgroundColor: snapshot.isDragging ? '#F8F9FA' : 'transparent',
                      position: 'relative'
                    }}
                  >
                    <Box
                      {...provided.dragHandleProps}
                      sx={{
                        position: 'absolute',
                        left: -24,
                        display: 'flex',
                        alignItems: 'flex-start',
                        paddingTop: '3px',
                        color: '#666666',
                        cursor: 'grab',
                        '&:active': {
                          cursor: 'grabbing'
                        }
                      }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </Box>
                    <Checkbox
                      checked={item.checked}
                      onChange={() => handleCheckboxChange(index)}
                      icon={<RadioButtonUncheckedIcon />}
                      checkedIcon={<CheckCircleIcon />}
                      sx={{ 
                        p: 0,
                        mr: 1,
                        mt: '3px',
                        color: '#666666',
                        '&.Mui-checked': {
                          color: '#3366FF'
                        },
                        '& .MuiSvgIcon-root': { 
                          fontSize: 20
                        }
                      }}
                    />
                    <TextField
                      fullWidth
                      variant="standard"
                      value={item.text}
                      onChange={(e) => handleTextChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, index)}
                      inputRef={el => inputRefs.current[index] = el}
                      placeholder={!item.text ? placeholder : ''}
                      multiline
                      sx={{
                        '& .MuiInput-root': {
                          fontSize: '14px',
                          textDecoration: item.checked ? 'line-through' : 'none',
                          color: item.checked ? '#666666' : '#1A1A1A',
                          whiteSpace: 'pre-wrap',
                          wordWrap: 'break-word'
                        },
                        '& .MuiInput-root:before, & .MuiInput-root:after': {
                          display: 'none'
                        }
                      }}
                    />
                  </Box>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </Box>
        )}
      </Droppable>
    </DragDropContext>
  );
}
